/**
 * Main App component
 *
 * Operator routes require a real cookie session (`RequireSession`,
 * backed by `state/authSession.tsx`'s `GET /api/auth/whoami`); public
 * driver/viewer/auth routes (`/share/.../viewer`, `/join/<CODE>`,
 * `/event/<id>`, `/login`, `/invite/<token>`) stay unwrapped/anonymous.
 * Only mounts the active tab's component to save resources.
 */

import { useEffect, useState } from 'react';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';

import ErrorBoundary from './components/ErrorBoundary';
import Navbar from './components/Navbar';
import VersionBadge from './components/VersionBadge';
import RequireSession from './components/RequireSession';
import Settings from './components/Settings';
import Fleet from './components/Fleet';
import ShareViewerPage from './pages/ShareViewerPage';
import PlaySessionPage from './pages/PlaySessionPage';
import JoinLanding from './pages/JoinLanding';
import EventViewerPage from './pages/EventViewerPage';
import LoginPage from './pages/LoginPage';
import InviteAcceptPage from './pages/InviteAcceptPage';
import TeamPage from './pages/TeamPage';
import EventTab from './pages/admin/EventTab';
import StagesTab from './pages/admin/StagesTab';
import RosterTab from './pages/admin/RosterTab';
import { CurrentEventProvider } from './state/currentEvent';
import { AuthSessionProvider } from './state/authSession';

// Legacy paths that pre-date the MVP navbar reshuffle. The Fleet tab
// absorbs their former real estate — monitoring + logs + settings
// widgets fold into the Fleet detail card / future user-pill menu
// rather than holding top-level tab slots. Soft-redirect rather than
// 404 so any deep-linked bookmarks land somewhere reasonable.
const LEGACY_REDIRECTS: Record<string, string> = {
  '/monitoring': '/fleet',
  '/logs': '/fleet',
  // Fleet-map tab removed — the event-scoped public viewer map is the
  // real map surface.
  '/map': '/fleet',
};

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const [fleetSelected, setFleetSelected] = useState<string | null>(null);

  // Soft-redirect legacy paths from before the MVP navbar reshuffle.
  // Effect runs once per pathname change; replace() so back-button
  // doesn't bounce the user back to the dead URL.
  //
  // MUST stay ABOVE the early-return route matches below: hooks have
  // to render in identical order on every render of this component,
  // and an early-return route renders 3 hooks where a normal route
  // renders 4 — client-side navigating between the two (e.g. /stages
  // → /event/1 → /fleet) threw "Rendered fewer hooks than expected"
  // and crashed to the ErrorBoundary. Harmless on the public routes:
  // LEGACY_REDIRECTS only matches /monitoring + /logs, so the effect
  // no-ops there.
  useEffect(() => {
    const target = LEGACY_REDIRECTS[currentPath];
    if (target) navigate(target, { replace: true });
  }, [currentPath, navigate]);

  // Public synced-viewer share route — no Navbar, no admin gate.
  // Pattern: /share/<token>/viewer
  const shareMatch = currentPath.match(/^\/share\/([^/]+)\/viewer\/?$/);
  if (shareMatch) {
    return <ShareViewerPageWrapper token={shareMatch[1]} />;
  }

  // Public event viewer — multi-device map at `/event/<id>`. Not wrapped
  // in RequireSession, no Navbar; spectators with the URL land on the map
  // directly. Numeric id here; the 6-char join_code path is handled
  // separately below.
  const eventMatch = currentPath.match(/^\/event\/(\d+)\/?$/);
  if (eventMatch) {
    return <EventViewerPage eventId={eventMatch[1]} />;
  }

  // Map-first public player, served beside the classic viewer above so
  // both stay reachable for the same event.
  const eventV2Match = currentPath.match(/^\/event\/(\d+)\/v2\/?$/);
  if (eventV2Match) {
    return <EventViewerPage eventId={eventV2Match[1]} layout="v2" />;
  }

  // Smart-link landing — what the admin Event tab QR poster encodes
  // (publicViewerUrl). A driver scanning the poster with a stock
  // camera lands here; the page tries `tuvion://join/<CODE>` and
  // falls back to an APK install CTA when the app isn't installed.
  // Path is intentionally /join/<CODE> (no /pwa/ prefix) so it
  // matches the canonical install-landing host. Code-shape matches
  // the backend's join-code alphabet. Not wrapped in RequireSession —
  // driver path.
  const joinMatch = currentPath.match(/^\/join\/([A-HJ-KMNPQR-Z2-9]{6})\/?$/i);
  if (joinMatch) {
    return <JoinLanding code={joinMatch[1].toUpperCase()} />;
  }

  // Cookie-session login. Public, no Navbar, no RequireSession (an
  // anonymous visitor is exactly who this page is for; RequireSession
  // redirects HERE when it denies a route).
  if (currentPath === '/login') {
    return <LoginPage />;
  }

  // Invite-accept landing. Public: the token in the URL IS the
  // credential (see InviteAcceptPage's own docstring), so this stays
  // unwrapped exactly like /login.
  const inviteMatch = currentPath.match(/^\/invite\/([^/]+)\/?$/);
  if (inviteMatch) {
    return <InviteAcceptPage token={decodeURIComponent(inviteMatch[1])} />;
  }

  // Operator session-player route — operator-facing, so RequireSession
  // applies. Navbar stays so the operator can navigate back to fleet.
  // Pattern: /play/<sid>. Session id pattern matches `_SESSION_ID_RE`
  // (`^[A-Za-z0-9._-]{1,64}$`).
  const playMatch = currentPath.match(/^\/play\/([A-Za-z0-9._-]{1,64})\/?$/);
  if (playMatch) {
    return (
      <RequireSession>
        <CurrentEventProvider>
          <Navbar />
          <PlaySessionPage sessionId={decodeURIComponent(playMatch[1])} />
        </CurrentEventProvider>
      </RequireSession>
    );
  }

  const renderContent = () => {
    switch (currentPath) {
      case '/':
      case '/fleet':
        return <Fleet selectedId={fleetSelected} onSelect={setFleetSelected} />;
      case '/event':
        return <EventTab />;
      case '/stages':
        return <StagesTab />;
      case '/roster':
        return <RosterTab />;
      // People/Team management. Reachable via direct URL for everyone
      // with a session (server-side require_authz is the real gate —
      // see TeamPage.tsx's own docstring); Navbar's nav entry is
      // hidden for a non-superadmin, but a non-organizer/non-superadmin
      // hitting this URL directly still just sees a clean 403 from the
      // team-people fetch, not a broken page.
      case '/team':
        return <TeamPage />;
      // No tab slot anymore but reachable via direct URL — the user-pill
      // menu will absorb settings in a later step. /monitoring + /logs
      // are intentionally NOT here; the LEGACY_REDIRECTS effect bounces
      // them to /fleet on mount.
      case '/settings':
        return <Settings />;
      default:
        return <Fleet selectedId={fleetSelected} onSelect={setFleetSelected} />;
    }
  };

  return (
    <RequireSession>
      <CurrentEventProvider>
        <Navbar />
        {renderContent()}
      </CurrentEventProvider>
    </RequireSession>
  );
}

const ShareViewerPageWrapper: React.FC<{ token: string }> = ({ token }) => {
  return <ShareViewerPage tokenOverride={token} />;
};

// Build stamp is an OPERATOR diagnostic — fans on the public surfaces
// (/event, /join, /share) shouldn't see "v dev · <sha>" floating over
// the page. Inside the router so it re-evaluates on SPA navigation.
// /login + /invite are also unwrapped/public — same reasoning: a
// driver landing on an emailed invite link, or an operator on the
// login screen, shouldn't see an internal build-provenance badge either.
const OperatorVersionBadge: React.FC = () => {
  const { pathname } = useLocation();
  const isPublic =
    /^\/event\/\d+(\/v2)?\/?$/.test(pathname) ||
    /^\/join\//.test(pathname) ||
    /^\/share\//.test(pathname) ||
    pathname === '/login' ||
    /^\/invite\//.test(pathname);
  if (isPublic) return null;
  return <VersionBadge />;
};

function App() {
  return (
    <ErrorBoundary>
      {/* Session/auth context, mounted OUTSIDE the router so both
          public routes (LoginPage, InviteAcceptPage) and
          RequireSession-gated operator routes can read it — see
          state/authSession.tsx. */}
      <AuthSessionProvider>
        <BrowserRouter>
          <AppContent />
          {/* Build stamp (fixed, bottom-right, operator routes only).
              Confirms which commit each surface runs after a deploy. */}
          <OperatorVersionBadge />
        </BrowserRouter>
      </AuthSessionProvider>
    </ErrorBoundary>
  );
}

export default App;
